import { formatScheduleCompact } from "./scheduleDisplay.js";

function offeringLabel(offering) {
  const instructor = offering.instructor_name || "";
  const title = offering.retreat_name || offering.title || "";
  if (!instructor) return title;
  const suffix = new RegExp(
    `\\s*with\\s*${instructor.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*$`,
    "i"
  );
  const cleaned = title.replace(suffix, "").trim();
  return cleaned || title;
}

export function formatProviderName(source) {
  const raw = String(source || "").trim().replace(/_/g, " ");
  if (!raw) return "School";
  return raw
    .split(/\s+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function buildScheduleLines(offerings) {
  const lines = [];
  const seen = new Set();

  for (const offering of offerings) {
    const schedule = formatScheduleCompact(offering.schedule_json, offering.schedule_summary);
    if (!schedule) continue;

    const title = offeringLabel(offering);
    const key = `${title}|${schedule}`;
    if (seen.has(key)) continue;
    seen.add(key);

    lines.push({ title, schedule });
  }

  return lines;
}

function buildSchoolScheduleLines(offerings, { hidePrices = false } = {}) {
  return offerings.map((offering) => {
    const title = offering.retreat_name || offering.title || "Course";
    const bits = [];
    if (offering.duration) bits.push(offering.duration);
    if (hidePrices) {
      bits.push("On request");
    } else if (offering.price_clean != null && Number.isFinite(Number(offering.price_clean))) {
      bits.push(`from $${offering.price_clean}`);
    } else if (offering.price && String(offering.price).trim()) {
      bits.push(String(offering.price).trim());
    } else {
      bits.push("Price on request");
    }
    return { title, schedule: bits.join(" · ") };
  });
}

function buildGroupedTeacherCard(offerings, { hidePrices = false } = {}) {
  const primary = offerings[0];
  const instructorName = primary.instructor_name || "";
  const scheduleLines = buildScheduleLines(offerings);
  const styles = [
    ...new Set(
      offerings.flatMap((offering) =>
        offering.styles?.length ? offering.styles : offering.style ? [offering.style] : []
      )
    ),
  ].filter(Boolean);
  const prices = hidePrices
    ? []
    : offerings
        .map((offering) => offering.price_clean)
        .filter((value) => value != null && Number.isFinite(Number(value)));

  const offeringLabels = [...new Set(offerings.map(offeringLabel).filter(Boolean))];
  const cardSubtitle =
    offerings.length === 1
      ? "1 live class"
      : `${offerings.length} live classes`;

  return {
    ...primary,
    item_source: "teacher_offering",
    source: primary.source || "yoga_teachers",
    retreat_key: `teacher-group-${primary.instructor_slug}`,
    is_teacher_group: true,
    is_school_group: false,
    grouped_offerings: offerings,
    retreat_name: instructorName || offeringLabels[0] || "Yoga instructor",
    card_subtitle: cardSubtitle,
    instructor_name: instructorName,
    instructor_slug: primary.instructor_slug,
    instructor_id: primary.instructor_id,
    schedule_lines: scheduleLines,
    styles,
    style: styles.join(", "),
    style_tags: styles.join(", "),
    price_clean: hidePrices ? null : prices.length ? Math.min(...prices) : primary.price_clean,
    duration: offerings.length > 1 ? `${offerings.length} class times` : primary.duration,
    level: "",
  };
}

function buildGroupedSchoolCard(offerings, { hidePrices = false } = {}) {
  const primary = offerings[0];
  const sourceKey = String(primary.source || "").trim().toLowerCase();
  const providerName = formatProviderName(primary.source);
  const styles = [
    ...new Set(
      offerings.flatMap((offering) =>
        offering.styles?.length ? offering.styles : offering.style ? [offering.style] : []
      )
    ),
  ].filter(Boolean);
  const prices = hidePrices
    ? []
    : offerings
        .map((offering) => offering.price_clean)
        .filter((value) => value != null && Number.isFinite(Number(value)));

  return {
    ...primary,
    item_source: "school_offering",
    source: primary.source,
    retreat_key: `school-group-${sourceKey}`,
    is_teacher_group: false,
    is_school_group: true,
    grouped_offerings: offerings,
    retreat_name: providerName,
    card_subtitle:
      offerings.length > 1 ? `${offerings.length} online courses` : offerings[0]?.retreat_name || "Online course",
    instructor_name: "",
    instructor_slug: "",
    provider_source: sourceKey,
    schedule_lines: buildSchoolScheduleLines(offerings, { hidePrices }),
    styles,
    style: styles.join(", "),
    style_tags: styles.join(", "),
    price_clean: hidePrices ? null : prices.length ? Math.min(...prices) : primary.price_clean,
    duration: offerings.length > 1 ? `${offerings.length} courses` : primary.duration,
    google_rating: primary.google_rating,
    google_review_count: primary.google_review_count,
    level: "",
  };
}

function isTeacherItem(item) {
  return item?.item_source === "teacher_offering" || item?.source === "yoga_teachers";
}

/**
 * Group online/prenatal listings:
 * - individual instructors → one card per instructor
 * - school providers (e.g. Yogkulam) → one card per source (unless teachersOnly)
 *
 * @param {object} [options]
 * @param {boolean} [options.hidePrices]
 * @param {boolean} [options.teachersOnly] - only yoga_teachers / teacher offerings (no school providers)
 */
export function groupTeacherOfferingsForListing(
  items,
  { hidePrices = false, teachersOnly = false } = {}
) {
  if (!Array.isArray(items) || items.length === 0) return items;

  const filtered = teachersOnly ? items.filter(isTeacherItem) : items;
  if (filtered.length === 0) return [];

  const offeringsByTeacher = new Map();
  const offeringsBySchool = new Map();
  const groupOpts = { hidePrices };
  const stripPrice = (item) =>
    hidePrices ? { ...item, price_clean: null, price: null } : item;

  for (const item of filtered) {
    if (isTeacherItem(item) && item.instructor_slug) {
      const list = offeringsByTeacher.get(item.instructor_slug) || [];
      list.push({ ...stripPrice(item), item_source: "teacher_offering" });
      offeringsByTeacher.set(item.instructor_slug, list);
      continue;
    }

    if (teachersOnly) continue;

    const sourceKey = String(item.source || "").trim().toLowerCase();
    if (sourceKey && sourceKey !== "yoga_teachers") {
      const list = offeringsBySchool.get(sourceKey) || [];
      list.push({ ...stripPrice(item), item_source: item.item_source || "retreat" });
      offeringsBySchool.set(sourceKey, list);
    }
  }

  const seenTeachers = new Set();
  const seenSchools = new Set();
  const grouped = [];

  for (const item of filtered) {
    if (isTeacherItem(item) && item.instructor_slug) {
      if (seenTeachers.has(item.instructor_slug)) continue;
      seenTeachers.add(item.instructor_slug);
      grouped.push(
        buildGroupedTeacherCard(offeringsByTeacher.get(item.instructor_slug) || [item], groupOpts)
      );
      continue;
    }

    if (teachersOnly) continue;

    const sourceKey = String(item.source || "").trim().toLowerCase();
    if (sourceKey && sourceKey !== "yoga_teachers") {
      const schoolOfferings = offeringsBySchool.get(sourceKey) || [item];
      // Only collapse when a school has 2+ courses (keep singles as normal retreat cards)
      if (schoolOfferings.length > 1) {
        if (seenSchools.has(sourceKey)) continue;
        seenSchools.add(sourceKey);
        grouped.push(buildGroupedSchoolCard(schoolOfferings, groupOpts));
        continue;
      }
    }

    grouped.push(stripPrice(item));
  }

  return grouped;
}

export const groupOnlineCourseListings = groupTeacherOfferingsForListing;
